/**
 * Whole-grade subject choice (`Capabilities/Assessment/Enrollment/SubjectChoicesController.cs`). Authoring the rules
 * lives in `SubjectChoiceRulesStore.ts`.
 *
 * Calls the shared `http` instance directly for the same reason `templates/CurriculumTemplatesStore.ts` does:
 * `npm run api` needs a running server to regenerate `APIClient.ts`. These endpoints are REST-shaped (bare
 * payload, real status codes), not the RPC envelope, so there is nothing to `unwrap`.
 */

import http from "../general/HttpClient";
import type { Finding } from "./subjectChoiceEvaluator";

export interface SubjectChoiceSubject {
    moduleID: string;
    name: string;
    termCount: number;
    credits?: number | null;
    /** Rules that name this subject explicitly. Rules over all subjects are not repeated here. */
    ruleIDs: string[];
}

export interface SubjectChoiceStudent {
    studentID: string;
    levelEnrollmentStatus?: string;
    moduleIDs: string[];
    /** Subjects with a Completed term. A save leaves those terms alone, so the screen locks the cell. */
    completedModuleIDs: string[];
    /** The server's view of this row, computed on every read. */
    findings: Finding[];
}

export interface SubjectChoiceRule {
    id: string;
    qualificationID: string;
    qualificationLevelID: string;
    name: string;
    description?: string | null;
    rank: number;
    appliesToAllSubjects: boolean;
    /** "Subjects" or "Credits". */
    measure: string;
    minimum?: number | null;
    maximum?: number | null;
    /** In the grid, narrowed to the subjects the grade offers. */
    moduleIDs: string[];
    /** Set when a curriculum template created the rule. */
    source?: string | null;
}

export interface SubjectChoiceCorequisite {
    moduleID: string;
    requiredModuleID: string;
}

export interface SubjectChoiceGrid {
    qualificationProgramLevelID: string;
    subjects: SubjectChoiceSubject[];
    students: SubjectChoiceStudent[];
    rules: SubjectChoiceRule[];
    corequisites: SubjectChoiceCorequisite[];
    /** Rules this grade cannot apply this year, and credit rules over subjects without credits. Never blocking. */
    gradeFindings: Finding[];
}

export interface SubjectChoiceSaveResult {
    levelEnrollmentsCreated: number;
    moduleEnrollmentsCreated: number;
    moduleEnrollmentsReactivated: number;
    moduleEnrollmentsDeactivated: number;
}

export interface StudentFinding {
    studentID: string;
    kind: string;
    ruleID?: string | null;
    message: string;
}

/** A save the server refused because students break the rules. Nothing was written. */
export class SubjectChoiceRulesError extends Error {
    readonly findings: StudentFinding[];

    constructor(message: string, findings: StudentFinding[]) {
        super(message);
        this.name = "SubjectChoiceRulesError";
        this.findings = findings;
    }
}

const BASE_URL = "/api/assessment/enrollment/subject-choices";

export const getSubjectChoices = (programLevelID: string): Promise<SubjectChoiceGrid> =>
    http.get<SubjectChoiceGrid>(`${BASE_URL}/${encodeURIComponent(programLevelID)}`).then((r) => r.data);

/**
 * Only the students passed are touched; each gets exactly the subjects listed. A rule rejection is rethrown as a
 * `SubjectChoiceRulesError` carrying every failing student, so the screen can mark each row.
 */
export const saveSubjectChoices = (
    programLevelID: string,
    students: Array<{ studentID: string; moduleIDs: string[] }>
): Promise<SubjectChoiceSaveResult> =>
    http
        .put<SubjectChoiceSaveResult>(`${BASE_URL}/${encodeURIComponent(programLevelID)}`, { students })
        .then((r) => r.data)
        .catch((error) => {
            const data = error?.response?.data;
            if (error?.response?.status === 400 && Array.isArray(data?.findings) && data.findings.length > 0)
                throw new SubjectChoiceRulesError(data.detail ?? "Some students break the subject-choice rules.", data.findings);
            throw error;
        });
